import { useSearchParams } from 'react-router-dom';
import './MembershipPaymentSuccessStripe.css';
import { useContext, useState } from 'react';
import { triggerMembershipSync } from '../../services/membership.service';
import { UserContext } from '../../contexts/user.context';
import { UserProviderValue } from '../../models';
import { openGolfnApp } from '../../utils';

const isMobileDevice = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// Free-trial length copy. The URL carries days (from cancelInDays on the offer),
// but a "1-month free" gift is really 28-31 days depending on the calendar
// month it activated in — showing "31-day free trial" on July activations reads
// awkward. Prefer whole-month wording when the day count is within ±3 days of
// N × 30.44 (avg calendar month). Falls back to raw days for odd durations
// (e.g. 14 days, 45 days) that don't sit near a month boundary.
const formatFreeTrialLength = (days: number): string => {
   if (days <= 0) return '';
   const AVG_MONTH_DAYS = 30.44;
   const nearestMonths = Math.round(days / AVG_MONTH_DAYS);
   if (nearestMonths >= 1 && Math.abs(days - nearestMonths * AVG_MONTH_DAYS) <= 3) {
      return `${nearestMonths}-month`;
   }
   return `${days}-day`;
};

export function MembershipPaymentSuccess() {
   const { user } = useContext<UserProviderValue>(UserContext);
   const [searchParams] = useSearchParams();
   const membershipType = searchParams.get('membershipType') || undefined;
   const membershipName = searchParams.get('membershipName') || undefined;
   const trialPeriodDaysRaw = searchParams.get('trialPeriodDays');
   const trialPeriodDays = Number.isFinite(Number(trialPeriodDaysRaw)) && Number(trialPeriodDaysRaw) > 0 ? Number(trialPeriodDaysRaw) : 0;
   const trialLengthLabel = formatFreeTrialLength(trialPeriodDays);
   const email = searchParams.get('email') || undefined;
   const name = searchParams.get('name') || undefined;
   const [isMobile] = useState(() => isMobileDevice());

   const prettyMembership = membershipName || membershipType || 'GolfN';

   const handleRedirectToApp = async () => {
      triggerMembershipSync();
      window.location.href = 'golfn://home';
   };

   const handleOpenApp = () => {
      triggerMembershipSync();
      openGolfnApp(user?.id);
   };

   return (
      <div className="stripe-success-root">
         <div className="stripe-success-card">
            <div className="stripe-checkmark">
               <svg
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <circle
                     cx="12"
                     cy="12"
                     r="12"
                     fill="#00b86b"
                  />
                  <path
                     fill="none"
                     stroke="#fff"
                     strokeWidth="2"
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     d="M7 13l3 3 7-7"
                  />
               </svg>
            </div>

            <h1 className="stripe-title">Membership Successfully Activated</h1>
            <p
               className="stripe-message"
               style={{ marginLeft: '-15px' }}>
               <span className="stripe-emoji">🏌️‍♂️</span> You just upgraded your game{name ? `, ${name}` : ''}!
            </p>

            {email && <p className="stripe-summary-line">{email}</p>}

            <div className="stripe-summary">
               {trialPeriodDays > 0 ? (
                  <p className="stripe-summary-line">
                     Your <strong>{trialLengthLabel} free trial</strong> is live.
                  </p>
               ) : (
                  <p className="stripe-summary-line">Your membership is live.</p>
               )}
               <p className="stripe-summary-line">
                  Enjoy <span className="stripe-summary-line-strong">{prettyMembership}</span> perks & earn more points every round.
               </p>
            </div>

            <div className="stripe-actions">
               <a
                  onClick={handleRedirectToApp}
                  className="stripe-btn-primary">
                  Start Playing
               </a>
               {isMobile && (
                  <a
                     onClick={handleOpenApp}
                     className="stripe-btn-open-app">
                     Open App
                  </a>
               )}
            </div>

            <p className="stripe-hint">It may take up to 30 seconds for your membership to appear in the app. If you don't see it, try closing and reopening the GolfN app.</p>
         </div>
      </div>
   );
}
